import { computed, ref, shallowRef } from 'vue'
import { useApiClient } from '~/services/api'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { resolveProblemMessageWithReference } from '~/services/api/resolveProblemMessageWithReference'
import { useLogger } from '~/composables/useLogger'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import type { LegalAgreementAcceptanceOutcome } from '~/composables/LegalAgreementAcceptanceOutcome'

/** Accepts a legal agreement; a failure resolves to a presentable message in `actionProblem`. */
export const useLegalAgreementAcceptance = () => {
  const legalAgreement = useApiClient().legalAgreement()
  const t = usePresentationTranslator()
  const logger = useLogger('useLegalAgreementAcceptance')

  const isAccepting = ref(false)
  const actionProblem = shallowRef<() => string>(() => '')

  const accept = async (legalAgreementId: string): Promise<LegalAgreementAcceptanceOutcome> => {
    isAccepting.value = true
    actionProblem.value = () => ''
    try {
      return { agreement: await legalAgreement.accept(legalAgreementId), isAccepted: true }
    } catch (error: unknown) {
      logger.error('accept', { error })
      const problem = AppProblemModel.normalize(error)
      actionProblem.value = () =>
        resolveProblemMessageWithReference(problem, t, t('common.legalAgreement.errors.accept'))
      return { isAccepted: false }
    } finally {
      isAccepting.value = false
    }
  }

  return { accept, actionProblem: computed(() => actionProblem.value()), isAccepting }
}
